import React, { useEffect, useRef, useState } from "react";
import {
  useStripe,
  useElements,
  PaymentElement,
  ExpressCheckoutElement,
} from "@stripe/react-stripe-js";
import type { StripeExpressCheckoutElementOptions } from "@stripe/stripe-js";
import { paymentsApi } from "../services/payments";
import type { ConfirmIntentDetails } from "../types/payments";
import { IS_NATIVE } from "../lib/stripeFlow";
import { nativeApplePayAvailable, payWithNativeApplePay, intentIdFromSecret } from "../lib/nativeApplePay";
import { publicUrl } from "../utils/publicUrl";
import { track } from "../lib/analytics";
import "./InlinePayOptions.css";
import { t as tr } from "../i18n";
import { T } from "../i18n/T";

export type Track = { name: string; artists: { name: string }[] };

type Props = {
  orderId: string | null;
  emailForLink: string;
  setEmailForLink: (v: string) => void;
  showCardForm: boolean;
  setShowCardForm: (v: boolean) => void;
  processing: boolean;
  setProcessing: (v: boolean) => void;
  payError: string | null;
  setPayError: (v: string | null) => void;
  selectedTrack: Track | null;
  totalToCharge: number;
  onPaymentSuccess?: (piId?: string) => void | Promise<void>;
  canDoubleBid?: boolean;
  onDoubleBid?: () => void;
  /** Per-product small print: the "song is played" money-trust
      line is only true for songs — it used to leak onto playlist and tip
      checkouts because this box is shared. Song shows the full block;
      tip shows Terms only (its card already carries the tip trust line);
      playlist/shop show NOTHING here — their page carries every line,
      always visible, before the wallet ever opens. */
  context?: "song" | "playlist" | "shop" | "tip";
  /** Personalizes the song context's supporting line. */
  djName?: string | null;
  /** Wallet-direct products (playlist, tip): Apple/Google Pay live INSIDE
      the big green CTA — the page itself must never grow a second wallet
      button (founder, build 34). Link + card stay as the other doors. */
  hideWallets?: boolean;
  /** The intent's client secret: inside the iOS app, Apple's own sheet
      confirms it (lib/nativeApplePay). Unused on the web. */
  clientSecret?: string | null;
  /** The intent's currency (ISO code) for the in-app Apple Pay sheet. */
  currency?: string;
  /** A song request's message and the fan's name: typed after the intent
      was created, so they ride along to /confirm-intent (2026-10-10).
      Absent for boosts, counters, tips and playlists. */
  confirmDetails?: ConfirmIntentDetails;
  /** A fan's song request: report the checkout steps (checkout_viewed /
      pay_tapped / payment_failed) for the Growth tab's fan-conversion
      card. Boosts and counters reuse context "song" but never set this. */
  trackCheckout?: boolean;
};

// Orders whose pay box was already reported as seen. Module-level because
// the song page re-mounts this box on every price change.
const viewedOrders = new Set<string>();

// Why a payment did not go through, as Stripe names it (decline code first:
// "insufficient_funds" says more than "card_declined").
const failReason = (err: any): string =>
  String(err?.decline_code || err?.code || err?.type || "unknown").slice(0, 60);

const InlinePayOptions: React.FC<Props> = ({
  orderId,
  showCardForm,
  setShowCardForm,
  processing,
  setProcessing,
  payError,
  setPayError,
  onPaymentSuccess,
  totalToCharge,
  canDoubleBid,
  onDoubleBid,
  context = "song",
  hideWallets,
  clientSecret,
  currency,
  confirmDetails,
  trackCheckout,
}) => {
  const stripe = useStripe();
  const elements = useElements();

  // Inside the iOS/Android app the page runs in a web view, where the web
  // wallets cannot complete: Apple disables Apple Pay JS in a WKWebView that
  // injects scripts (Capacitor does), and the app's origin is not a domain
  // Stripe can register. The button rendered and did nothing (build 41, the
  // first build with the fan path open). Offer Link + card there; Safari and
  // the web are unchanged.
  const walletsOff = hideWallets || IS_NATIVE;

  // In-app Apple Pay: Apple's own sheet through the Stripe iOS SDK, confirming
  // the same intent. Shown only where the device can present it; the tip and
  // boost blocks carry it too (their green CTAs cannot open a wallet here).
  const [nativeApplePay, setNativeApplePay] = useState(false);
  useEffect(() => {
    if (!IS_NATIVE || !clientSecret) return;
    let alive = true;
    nativeApplePayAvailable().then((ok) => {
      if (alive) setNativeApplePay(ok);
    });
    return () => {
      alive = false;
    };
  }, [clientSecret]);

  // Where a redirect-based confirmation (3DS etc.) comes back: the live
  // card on My Requests (the confirmation page is retired). In the phone app
  // such a redirect has already left for the browser, where only a
  // requesta.app address opens (publicUrl); the website is unchanged.
  const successUrl = publicUrl(
    "/fan/requests" + (orderId ? `?order=${encodeURIComponent(orderId)}` : "")
  );

  const expressOptions: StripeExpressCheckoutElementOptions = {
    paymentMethodOrder: ["apple_pay", "google_pay", "link", "card"],
    buttonType: { applePay: "plain", googlePay: "buy" },
    layout: { maxColumns: 1 } as any,
    // Fan status emails (Phase 3): the wallet sheet shows an Email line
    // pre-filled from the payer's Apple/Google account — no typing in the
    // normal case. The address lands on the charge's billing details, where
    // /confirm-intent stores it. Storing it can never block the payment.
    emailRequired: true,
    // hideWallets: Link may still render (it's a non-wallet fast door);
    // Apple/Google Pay never re-appear on the page — the green CTA re-opens
    // them instead.
    ...(walletsOff
      ? { paymentMethods: { applePay: "never" as const, googlePay: "never" as const } }
      : {}),
  };

  const confirmingRef = useRef(false);

  // Checkout steps for the fan-conversion card. Analytics only: never
  // throws, never awaited, never changes what the fan sees. track() only
  // queues (the batch goes out 0.8 s later, or on page hide); the try is
  // belt and braces, so a broken tracker can never stop a payment.
  const step = (name: "checkout_viewed" | "pay_tapped" | "payment_failed", props: Record<string, unknown>) => {
    try {
      if (trackCheckout && orderId) track(name, { screen: "song_checkout", props: { orderId, ...props } });
    } catch {
      /* analytics never touches the payment */
    }
  };
  // What the wallet button offered on this device ({ applePay: true, … }).
  const walletsRef = useRef<string[] | null>(null);

  // Stripe's Express Checkout (Apple Pay / Google Pay) button renders
  // asynchronously after mount. Keep a pulsing placeholder in its slot until
  // `onReady` fires so the customer never sees a blank gap. The timeout is a
  // safety net in case `onReady` never fires (e.g. Stripe.js fails to load).
  const [expressReady, setExpressReady] = useState(false);
  useEffect(() => {
    if (expressReady) return;
    const t = setTimeout(() => setExpressReady(true), 8000);
    return () => clearTimeout(t);
  }, [expressReady]);

  useEffect(() => {
    if (!expressReady || !trackCheckout || !orderId || viewedOrders.has(orderId)) return;
    viewedOrders.add(orderId);
    step("checkout_viewed", {
      wallets: [...(walletsRef.current || []), ...(nativeApplePay ? ["apple_pay_in_app"] : [])],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expressReady, orderId, trackCheckout]);

  const confirmOnServer = async (piId?: string) => {
    if (!orderId) return;
    try {
      await paymentsApi.confirmIntent(orderId, piId, confirmDetails);
    } catch (e) {
      console.error("confirm-intent error:", e);
    }
  };

  const runConfirm = async (returnUrl: string, method: string) => {
    if (!stripe || !elements) return { ok: false, msg: tr("inlinePayOptions.stripeNotReady") };
    if (confirmingRef.current) return { ok: false, msg: tr("inlinePayOptions.alreadyConfirming") };
    confirmingRef.current = true;
    setProcessing(true);
    setPayError(null);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      step("payment_failed", { method, reason: failReason(submitError) });
      confirmingRef.current = false;
      setProcessing(false);
      return { ok: false, msg: submitError.message || tr("inlinePayOptions.paymentError") };
    }

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
      redirect: "if_required",
    });

    if (!error) {
      return { ok: true, piId: paymentIntent?.id };
    }

    if ((error as any)?.code === "payment_intent_unexpected_state") {
      const piId =
        (error as any)?.payment_intent?.id ||
        (error as any)?.payment_intent_client_secret?.split("_secret")[0];
      // The same error comes back for an intent that can never be paid — a
      // cancelled one (the bid went A → B → A and the first intent came
      // back, 2026-10-10). Only an intent that really holds the money counts
      // as paid, asked of Stripe; anything else gets the error below.
      let status: string | undefined = error.payment_intent?.status;
      const secret = clientSecret || error.payment_intent?.client_secret;
      if (secret) {
        try {
          status = (await stripe.retrievePaymentIntent(secret)).paymentIntent?.status ?? status;
        } catch {
          /* Stripe unreachable: the state the error carried decides */
        }
      }
      if (status === "requires_capture" || status === "succeeded" || status === "processing") {
        return { ok: true, piId };
      }
    }

    step("payment_failed", { method, reason: failReason(error) });
    confirmingRef.current = false;
    setProcessing(false);
    setPayError(error.message || tr("inlinePayOptions.paymentError"));
    return { ok: false, msg: error.message || tr("inlinePayOptions.paymentError") };
  };

  const handleExpressConfirm = async (event: any) => {
    if (!stripe || !elements) {
      event.complete && event.complete("fail");
      return;
    }

    const method = String(event?.expressPaymentType || "wallet");
    step("pay_tapped", { method });
    const res = await runConfirm(successUrl, method);
    if (!res.ok) {
      event.complete && event.complete("fail");
      return;
    }

    await confirmOnServer(res.piId);

    event.complete && event.complete("success");
    confirmingRef.current = false;
    setProcessing(false);

    if (onPaymentSuccess) await onPaymentSuccess(res.piId);
    else if (orderId)
      window.location.href = `${window.location.origin}/fan/requests?order=${encodeURIComponent(
        orderId
      )}`;
  };

  const payWithApplePayInApp = async () => {
    if (!clientSecret || processing || confirmingRef.current) return;
    step("pay_tapped", { method: "apple_pay_in_app" });
    confirmingRef.current = true;
    setProcessing(true);
    setPayError(null);
    const res = await payWithNativeApplePay({
      clientSecret,
      amount: totalToCharge,
      currency: currency || "usd",
    });
    confirmingRef.current = false;
    if (res !== "success") {
      step("payment_failed", { method: "apple_pay_in_app", reason: res === "canceled" ? "cancelled" : "apple_pay_failed" });
      setProcessing(false);
      if (res === "failed") setPayError(tr("inlinePayOptions.applePayDidnT"));
      return;
    }
    const piId = intentIdFromSecret(clientSecret);
    await confirmOnServer(piId);
    setProcessing(false);
    if (onPaymentSuccess) await onPaymentSuccess(piId);
    else if (orderId)
      window.location.href = `${window.location.origin}/fan/requests?order=${encodeURIComponent(
        orderId
      )}`;
  };

  const confirmCard = async () => {
    step("pay_tapped", { method: "card" });
    const res = await runConfirm(successUrl, "card");
    if (!res.ok) return;

    await confirmOnServer(res.piId);

    confirmingRef.current = false;
    setProcessing(false);
    await onPaymentSuccess?.(res.piId);
  };

  return (
    <div className="rq-inline-pay">
      <div className="rq-wallets">
        {canDoubleBid && onDoubleBid && (
          <button
            type="button"
            className="rq-double-bid-btn"
            onClick={onDoubleBid}
            disabled={processing}
          >
            {tr("inlinePayOptions.doubleYourBid")}
          </button>
        )}

        <div className="rq-express-vertical">
          {nativeApplePay && (
            <button
              type="button"
              className="rq-fake-express-slot rq-native-applepay"
              onClick={payWithApplePayInApp}
              disabled={processing}
              aria-label={tr("inlinePayOptions.payWithApplePay")}
            >
              <span className="rq-fake-express-slot__icon" aria-hidden>
                <svg width="16" height="20" viewBox="0 0 814 1000" fill="currentColor" focusable="false">
                  <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.3 39.5-164.4 39.5-68.1 0-120.8-60.4-163.7-122.2C37.1 790.2 0 663.3 0 538.5 0 344.3 126.2 227.2 279.1 227.2c68.1 0 124.4 40.7 167.2 40.7 40.2 0 103.2-43.2 190.8-43.2 30.8 0 142.1 2.7 242.1 108.2zM585.1 152.8c35.3-41.9 61.1-99.8 61.1-158.4 0-8.2-.6-16.5-1.9-24.8-58.1 2.4-126.2 38.9-168.1 87.5-32.3 36.8-61.9 94.8-61.9 153.4 0 8.9.7 17.7 2.1 25.8 4.7.4 9.5.6 14.3.6 47.4 0 93.4-24.7 134.5-63.1z" />
                </svg>
              </span>
              <span className="rq-fake-express-slot__label">{tr("inlinePayOptions.pay")}</span>
            </button>
          )}
          <div className={`rq-express-slot${expressReady ? "" : " is-loading"}`}>
            <ExpressCheckoutElement
              options={expressOptions}
              onConfirm={handleExpressConfirm}
              onReady={(e: any) => {
                const offered = e?.availablePaymentMethods || {};
                walletsRef.current = Object.keys(offered).filter((k) => offered[k]);
                setExpressReady(true);
              }}
              // The fan opened Apple/Google Pay and closed the sheet without
              // paying — the "saw the total and changed their mind" moment.
              onCancel={() => {
                step("pay_tapped", { method: "wallet" });
                step("payment_failed", { method: "wallet", reason: "cancelled" });
              }}
            />
            {!expressReady && !IS_NATIVE && (
              <div
                className="rq-express-skeleton rq-pay-loading-placeholders"
                aria-hidden="true"
              >
                <div className="rq-fake-express-slot">
                  <span className="rq-fake-express-slot__icon" aria-hidden>
                    <svg
                      width="16"
                      height="20"
                      viewBox="0 0 814 1000"
                      fill="currentColor"
                      focusable="false"
                    >
                      <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.3 39.5-164.4 39.5-68.1 0-120.8-60.4-163.7-122.2C37.1 790.2 0 663.3 0 538.5 0 344.3 126.2 227.2 279.1 227.2c68.1 0 124.4 40.7 167.2 40.7 40.2 0 103.2-43.2 190.8-43.2 30.8 0 142.1 2.7 242.1 108.2zM585.1 152.8c35.3-41.9 61.1-99.8 61.1-158.4 0-8.2-.6-16.5-1.9-24.8-58.1 2.4-126.2 38.9-168.1 87.5-32.3 36.8-61.9 94.8-61.9 153.4 0 8.9.7 17.7 2.1 25.8 4.7.4 9.5.6 14.3.6 47.4 0 93.4-24.7 134.5-63.1z" />
                    </svg>
                  </span>
                  <span className="rq-fake-express-slot__label">{tr("inlinePayOptions.pay")}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {!showCardForm ? (
          <button
            className="rq-add-card"
            onClick={() => {
              step("pay_tapped", { method: "card_form" });
              setShowCardForm(true);
            }}
          >
            {tr("inlinePayOptions.addCardDetails")}
          </button>
        ) : (
          <div className="rq-card-form">
            <PaymentElement />
            <button
              className="rq-pay-now"
              onClick={confirmCard}
              disabled={processing || !stripe || !elements}
            >
              {processing ? tr("inlinePayOptions.processing") : tr("inlinePayOptions.payNow")}
            </button>
          </div>
        )}

        {payError && <div className="rq-pay-error">{payError}</div>}

        {/* Terms is the last line, under the card door (founder,
            2026-09-27). The song promise and the "supporting {DJ}" line
            that used to sit up here are gone: the song page's summary panel
            (SongCheckout) already carries the promise, and playlist/shop
            pages carry their own lines. */}
        {context !== "playlist" && context !== "shop" && (
          <div className="rq-pay-disclosure">
            <T k="inlinePayOptions.byPayingYouAgree" tags={{ 0: (c) => <a href="/terms" target="_blank" rel="noopener noreferrer">{c}</a> }} />
          </div>
        )}
      </div>
    </div>
  );
};

export default InlinePayOptions;
